import { Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import Loader from "@repo/legacy/components/shared/Loader";
import ConfirmationDialog from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import {
	useDeleteProjectAdditionalInformationMutation,
	useGetAvailableServicesForProjectLazyQuery,
} from "@repo/server-api";
import { useCurrencyFormatter } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useRef, useState } from "react";

import { DialogProjectPosition } from "../dialogs/DialogProjectPosition";

import type { ProjectEditProps } from "./ProjectEditProps";
import type { DialogProjectPositionHandle } from "../dialogs/DialogProjectPosition";
import type { ProjectPosition, ProjectSaleableService } from "../types/ProjectTypes";
import type { GridColDef } from "@mui/x-data-grid/models";
import type { GridCellParams } from "@mui/x-data-grid-premium";
import type { ConfirmationDialogHandle } from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";

export const ProjectPositionsList = (props: ProjectEditProps) => {
	const { t } = useTranslation();
	const { project, setProject } = props;
	const formatCurrency = useCurrencyFormatter();

	const confirmationRef = useRef<ConfirmationDialogHandle | null>(null);
	const DialogProjectPositionHandleRef = useRef<DialogProjectPositionHandle | null>(null);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [services, setServices] = useState<ProjectSaleableService[]>([]);

	const [deletePositionMutation] = useDeleteProjectAdditionalInformationMutation();
	const deletePosition = async (informationId: string, projectId: string) => {
		return deletePositionMutation({
			variables: {
				informationId,
				projectId,
			},
			onCompleted(res) {
				if (res.deleteProjectAdditionalInformation.boolean !== true) return;
				// Remove position from project state
				setProject((prev) => ({
					...prev,
					positions: prev.positions.filter((p) => p.id !== informationId),
				}));
			},
		});
	};

	const addOrUpdatePosition = (position: ProjectPosition) => {
		// If a new id has been generated, add the position to the list
		const existingIndex = project.positions.findIndex((p) => p.id === position.id) !== -1;

		// Sort positions after adding or updating
		const updatedPositions = existingIndex
			? project.positions.filter((p) => p.id !== position.id)
			: [...project.positions];
		updatedPositions.push(position);

		updatedPositions.sort((a, b) => {
			if (!a.service || !b.service) return 0;
			return a.service.name.localeCompare(b.service.name);
		});

		setProject((prev) => ({
			...prev,
			positions: updatedPositions,
		}));
	};

	const [loadAvailableServicesForProject] = useGetAvailableServicesForProjectLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			projectId: props.project?.id ?? "",
		},
		onCompleted(data) {
			let loadedServices: ProjectSaleableService[] = [];
			if (data) {
				loadedServices = data.availableServicesForProject
					.flatMap((x) => x.services)
					.map((x) => ({ id: x.id, name: x.name, price: x.sellingPrice }));

				setServices(loadedServices);
			} else {
				setServices([]);
			}

			setIsLoading(false);
		},
	});

	const editPositionInList = (position: ProjectPosition) => {
		DialogProjectPositionHandleRef.current?.show({
			services,
			projectId: props.project!.id,
			position,
		});
	};

	useEffect(() => {
		// Only load services if we have a valid project ID
		if (!props.project.id) {
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		loadAvailableServicesForProject();
	}, [props.project.id, loadAvailableServicesForProject]);

	const columns: GridColDef<ProjectPosition>[] = [
		{
			field: "saleableService",
			headerName: t("services"),
			width: 300,
			renderCell: (params) => {
				return params.row.service?.name ?? "";
			},
		},
		{
			field: "billing-profile",
			headerName: t("billing-profile"),
			width: 300,
			renderCell: (params) => {
				if (params.row.accountingProfile) {
					// Render price per hour with Euro icon
					return <span>{params.row.accountingProfile.name}</span>;
				} else {
					// Render total price with Euro icon
					return <span>{" --- "}</span>;
				}
			},
		},
		{
			field: "price",
			headerName: t("price"),
			width: 300,
			renderCell: (params) => {
				if (params.row.hours) {
					// Render price per hour with Euro icon
					return (
						<span>
							{params.row.hours} {t("hours")} {" @ "} {formatCurrency(params.row.pricePerHour)}
						</span>
					);
				} else {
					// Render total price with Euro icon
					return (
						<span>
							{formatCurrency(params.row.price)} {" @ "}{" "}
							{t(`price-rate.${params.row.priceRate?.toLocaleLowerCase()}`, { ns: "enum" })}
						</span>
					);
				}
			},
		},

		{
			field: "volume",
			headerName: t("volume"),
			width: 300,
			renderCell: (params) => {
				if (params.row.totalPrice) {
					// Render total price with formatter
					return <span>{formatCurrency(params.row.totalPrice)}</span>;
				} else {
					// Render placeholder
					return <span>{" --- "}</span>;
				}
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			align: "center",
			sortable: false,
			width: 200,
			renderCell: (params) => {
				const openDelete = () =>
					confirmationRef.current?.show({
						text: t("do-you-really-want-to-delete-this-position"),
						onYes: async () => deletePosition(params.row.id, props.project.id),
					});

				const openEdit = () => {
					editPositionInList(params.row);
				};

				return (
					<div>
						<Button
							onClick={() => {
								openEdit();
							}}
							data-testid="position-edit-button">
							<Edit />
						</Button>
						<Button onClick={() => openDelete()} data-testid="position-delete-button">
							<DeleteIcon />
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div>
			{!isLoading ? (
				<div>
					<ConfirmationDialog ref={confirmationRef} />
					<DialogProjectPosition
						ref={DialogProjectPositionHandleRef}
						onSaved={addOrUpdatePosition}
					/>

					<Button
						className="left blueButton" sx={{ mb: 3 }}
						variant="contained"
						onClick={() => {
							DialogProjectPositionHandleRef.current?.show({
								services,
								projectId: props.project!.id,
							});
						}}
						data-testid="add-position-button">
						{t("add-new-position")}
						<AddIcon />
					</Button>

					<div className="clear" />
					<Box style={{ height: "65vh", width: "100%" }}>
						<DataGridPremium
							getRowId={(row: ProjectPosition) => row.id}
							columns={columns as GridCellParams[]}
							rows={project.positions}
							rowCount={project.positions?.length ?? 0}
							pageSizeOptions={[10, 20, 50, 100]}
							paginationMode="server"
							paginationModel={{ pageSize: 10, page: 0 }}
							disableRowSelectionOnClick
							hideFooter
							className="table"
						/>
					</Box>
				</div>
			) : (
				<Loader />
			)}
		</div>
	);
};
